/**
 * Feature Display Metadata
 *
 * Human-readable names, descriptions, and icons for subscription features.
 * Used by the UpgradePrompt dialog and pricing page.
 *
 * Single-tier model: all features included in YouGuard plan.
 * This file is pure data — safe to import from both server and client code.
 */

import type { Feature, SubscriptionTier } from "@/lib/subscription";

// ============================================
// Types
// ============================================

export interface FeatureDisplayInfo {
  /** User-facing feature name */
  name: string;
  /** Brief description of what the feature does */
  description: string;
  /** lucide-react icon name (resolved by consumer) */
  iconName: string;
  /** Tier that includes this feature (always "youguard" now) */
  requiredTier: SubscriptionTier;
}

export interface PlanDisplayInfo {
  /** User-facing plan name */
  name: string;
  /** Monthly price in USD */
  monthlyPrice: number;
  /** Annual price in USD */
  annualPrice: number;
  /** Whop plan ID for monthly checkout */
  monthlyPlanId: string;
  /** Whop plan ID for annual checkout */
  annualPlanId: string;
}

// ============================================
// Feature Metadata
// ============================================

type GatableFeature = Exclude<Feature, "maxChildren" | "emailAlerts">;

export const FEATURE_DISPLAY: Record<GatableFeature, FeatureDisplayInfo> = {
  smsMonitoring: {
    name: "SMS Monitoring",
    description:
      "Monitor text messages sent and received on connected devices.",
    iconName: "MessageCircle",
    requiredTier: "youguard",
  },
  callMonitoring: {
    name: "Call Log Visibility",
    description:
      "See who's calling and track call patterns on connected devices.",
    iconName: "Phone",
    requiredTier: "youguard",
  },
  browserExtension: {
    name: "Browser Shield",
    description:
      "Track browsing activity and block unsafe content with our browser extension.",
    iconName: "Globe",
    requiredTier: "youguard",
  },
  aiAnalysis: {
    name: "AI Content Analysis",
    description:
      "Get AI-powered safety analysis of YouTube channels and videos.",
    iconName: "Sparkles",
    requiredTier: "youguard",
  },
  contentRules: {
    name: "Custom Content Rules",
    description:
      "Set up custom rules to automatically flag or block content.",
    iconName: "Shield",
    requiredTier: "youguard",
  },
  sharedGuardians: {
    name: "Shared Guardians",
    description:
      "Invite other family members to help monitor and protect your children.",
    iconName: "Users",
    requiredTier: "youguard",
  },
  adultAccountability: {
    name: "Adult Accountability",
    description:
      "Set up accountability partnerships between adults for mutual support.",
    iconName: "Handshake",
    requiredTier: "youguard",
  },
};

// ============================================
// Plan Metadata
// ============================================

/** Single plan with monthly and annual options. */
export const PLAN_DISPLAY: PlanDisplayInfo = {
  name: "YouGuard",
  monthlyPrice: 9.99,
  annualPrice: 94.99,
  monthlyPlanId: "plan_Eqv8ysctUkNap",   // $9.99/mo with 30-day trial
  annualPlanId: "plan_KhQzI4zL39OIB",     // $94.99/yr with 30-day trial
};

// ============================================
// All Features List (for pricing page)
// ============================================

export const ALL_FEATURES = [
  "Monitor up to 5 children",
  "YouTube subscription & liked video tracking",
  "AI-powered content analysis",
  "Browser Shield extension",
  "Custom content rules",
  "Email alerts & notifications",
  "Shared guardian access",
  "Adult accountability partnerships",
  "Cross-channel scam protection",
  "Comments monitoring",
] as const;

// ============================================
// Child Slot Limit Display
// ============================================

export const CHILD_SLOT_DISPLAY = {
  name: "Monitored Children",
  description: "Monitor up to 5 children with your YouGuard subscription.",
  iconName: "UserPlus",
};

// ============================================
// Helpers
// ============================================

/**
 * Given a feature name, return the tier that unlocks it.
 * With single-tier model, always returns "youguard".
 */
export function getRequiredTierForFeature(
  feature: string,
): SubscriptionTier {
  const info =
    FEATURE_DISPLAY[feature as keyof typeof FEATURE_DISPLAY];
  return info?.requiredTier ?? "youguard";
}

/**
 * Get display info for a feature, or null if unknown.
 */
export function getFeatureDisplay(
  feature: string,
): FeatureDisplayInfo | null {
  return (
    FEATURE_DISPLAY[feature as keyof typeof FEATURE_DISPLAY] ?? null
  );
}
